import {ViewModelType} from "../../models/type/view-model-type.enum";
import {TableOperationModel} from "../../models/table-operation/table-operation-model";
import {UtilService} from "../util/util.service";
import {ContentModel} from "../../models/content/content-model";
import {RequestService} from "../request/request.service";
import {ContentDataService} from "../content-data/content-data.service";
import {ListContentModel} from "../../models/content/list-content-model";
import _ from "lodash";
import {DetailContentModel} from "../../models/content/detail-content-model";
import {KanbanCardContentModel} from "../../models/content/kanban-card-content-model";

export class TableOperationExecution {
  tableName: string;
  operation: TableOperationModel;
  contentModel: ContentModel;
  options: any;
  formType: ViewModelType;
  requestService: RequestService;
  koViewModel: any; // KO version of the content model used when executing operations

  constructor(tableName: string, operation: TableOperationModel, contentModel: ContentModel, options: any, formType: ViewModelType, requestService: RequestService) {
    this.tableName = tableName;
    this.operation = operation;
    this.contentModel = contentModel;
    this.options = options;
    this.formType = formType;
    this.requestService = requestService;
  }

  processCondition(record: any): boolean {
    const operation = this.operation;
    const options = this.options || {};
    const tableRecord = this.getTableRecordFromRawData(record, this.contentModel);

    let result = true;

    if (operation.hasCondition() && operation.executeConditionOnClient()) {
      try {
        result = UtilService.evalWithArguments(
          operation.getCondition(),
          this.getStandardContextItems(tableRecord, [tableRecord], options));
      } catch (ex) {
        console.error("TableOperationExecution.processCondition", ex);
        return false;
      }
    }

    return result;
  }

  execute(record: any) {
    // We allow a client side action to return a result to indicate that the table operation should be implemented
    // in some other way, e.g. by an Angular override. Additionally, the onBefore might return a promise (e.g.
    // for popping up a modal component) so we need to handle that.
    let result = undefined;
    const operation = this.operation;
    const options = this.options || {};
    const tableRecord = this.getTableRecordFromRawData(record, this.contentModel);
    const tableRecords = tableRecord ? [tableRecord] : [];

    let onBeforeResult = this.processOnBeforeAction(tableRecord, tableRecords, options);

    if (onBeforeResult instanceof Promise) {
      onBeforeResult.then((_result: any) => {

        const standardisedResult = this.standardizeOnBeforePromiseResult(_result, options);

        if (standardisedResult.result) {
          if (operation.isClientOperation()) {
            this.processClientSideAction(tableRecord, standardisedResult.data);
          } else {
            this.processServerSideAction(tableRecord, this.processPrepareDataAction(record), standardisedResult.data);
          }
          this.processOnAfterSubmit(tableRecord, standardisedResult.data);
        }
        // This doesn't make sense
        // return Promise.resolve(onBeforeBooleanResult);
      });
      // Return the promise
      result = onBeforeResult;
    } else {
      const standardisedResult = this.standardizeOnBeforePromiseResult(onBeforeResult, options);
      if (standardisedResult.result) {
        if (operation.isClientOperation()) {
          result = this.processClientSideAction(tableRecord, options);
        } else {
          this.processServerSideAction(tableRecord, this.processPrepareDataAction(record), options);
        }
        this.processOnAfterSubmit(tableRecord, options);
      }
    }
    return result;
  }

  executeMultiple(records: any[]) {
    const operation = this.operation;
    const options = this.options || {};
    const acceptsArrayOfRecords = operation.isBulkOperation();

    if (acceptsArrayOfRecords) {

      const tableRecords = records.map((record) => this.getTableRecordFromRawData(record, this.contentModel));
      const dataArray = records.map((record) => this.processPrepareDataAction(record));

      let onBeforeResult = this.processOnBeforeAction(tableRecords[0], tableRecords, options);

      if (onBeforeResult instanceof Promise) {
        onBeforeResult.then((_result: any) => {

          const standardisedResult = this.standardizeOnBeforePromiseResult(_result, options);

          if (standardisedResult.result) {
            if (operation.isClientOperation()) {
              this.processBulkClientSideAction(tableRecords, standardisedResult.data);
            } else {
              this.processBulkServerSideAction(tableRecords, dataArray, standardisedResult.data);
            }
          }
        });
      } else {
        const standardisedResult = this.standardizeOnBeforePromiseResult(onBeforeResult, options);
        if (standardisedResult.result) {
          if (operation.isClientOperation()) {
            this.processBulkClientSideAction(tableRecords, options);
          } else {
            this.processBulkServerSideAction(tableRecords, dataArray, options);
          }
        }
      }
    } else {
      records.forEach((record) => this.execute(record));
    }
  }

  /**
   * Standardises the onBefore Promise Result - which can be one of two formats - to a standard result.
   *
   * @param promiseResult The value returned from the OnBefore Table Operation
   * @param options A map of options that will be merged with the promise result if present
   */
  private standardizeOnBeforePromiseResult(promiseResult: any, options: any): { result: boolean, data: any } {
    // result can be one of two things
    // 1- a simple boolean that indicates we have actioned the OnBefore and should continue with the next operation (e.g. a simple onBefore Promise)
    // 2- an object with 2 parts
    //    - result: boolean same as option 1
    //    - data: data to be passed to the context of the next phase

    options = options || {};

    let onBeforeBooleanResult: boolean;
    let onBeforeData: any = {};

    if (typeof promiseResult === "boolean") {
      // variable is a boolean
      onBeforeBooleanResult = promiseResult;
    } else {
      // Variable is the object type
      onBeforeBooleanResult = (promiseResult.result === true);
      onBeforeData = promiseResult.data;
    }

    _.defaults(onBeforeData, options);

    return {
      result: onBeforeBooleanResult,
      data: onBeforeData
    };
  }

  private processOnBeforeAction(tableRecord: any, tableRecords: any[], options: any): Promise<any> {
    const operation = this.operation;
    let result: any = true;

    if (operation.hasOnBeforeScript()) {

      // Save the state of the Required setting for all fields on the record and do it just the once.
      // This is done so that workflow transition scripts setting a field to required(true) don't need to reset
      // the required setting if they are conditional and the inputs change such that fields previously required
      // are not now required.
      tableRecords.forEach(r => {
        r.saveRequiredState(true);
      });

      // If the table operation is being carried out in a list context we load up full record(s) as the before script
      // might reference fields that are not in the list (as it is also written to work on a detail form).
      // TODO: changes made to the transientTableRecords are not reflected in the real tableRecords.
      if (this.formType == ViewModelType.LIST) {
        const transientTableRecords = tableRecords.map((r) => {
          let record = null;
          let viewDefinition = null;
          let tableDefinition = null;

          const requestData = Object();
          requestData.id = r.getID();

          // If the related list is on a form as standard list, we need to use the parent to look for read privileges
          if (options.parentRelation !== null) {
            requestData.parentTable = options.parentTable;
            requestData.parentId = options.parentId;
            requestData.parentRelation = options.parentRelation;
          }

          this.requestService.createRequest().controller("servicely.core.System")
            .method("get")
            .table(r.getTableName())
            .view("get")
            .data(requestData)
            .async(false)
            .relationships()
            .successFunc((response) => {
              record = response.data;
              viewDefinition = response.viewDefinition;
              tableDefinition = response.tableDefinition;
            })
            .errorFunc((response) => {
              console.error("TableOperationExecution.processOnBeforeAction returned error", response);
            })
            .execute();

          if (record) {
            const transientContentModel = new ListContentModel(viewDefinition, tableDefinition, record);
            record = this.getTableRecordFromRawData(record, transientContentModel, true);
          }
          return record;
        });
        const transientTableRecord = transientTableRecords.find((r) => r.getID() === tableRecord.getID());

        if (transientTableRecord) {
          tableRecord = transientTableRecord;
          tableRecords = transientTableRecords;
        }
      }

      try {
        result = UtilService.evalWithArguments(
          operation.getOnBeforeScript(),
          this.getStandardContextItems(tableRecord, tableRecords, options, {
            id: tableRecord?.getID(),
            ids: tableRecords.map(r => r?.getID())
          }));
      } catch (ex) {
        console.error("TableOperationExecution.processOnBeforeAction threw exception", ex);
        this.sendOperationFailureEvent(tableRecord, operation, [ex]);
        // this.contentModel.getEventExecutor()._reEnableButton(); // This seems wrong as the UIEventExecutor does not have this method
        result = false;
      }
    }

    // Also run the 'On form submit' event handlers if this operation has been
    // configured for this purpose, e.g. 'Update'
    if (result instanceof Promise) {
      return result.then((result: boolean) => {
        return Promise.resolve(
            this._processOnFormSubmitAction(result, operation, options)
        );
      });
    } else {
      result = this._processOnFormSubmitAction(result, operation, options);
    }

    return result;
  }

  private _processOnFormSubmitAction(onBeforeResult, operation, options) {
    const standardisedResult = this.standardizeOnBeforePromiseResult(onBeforeResult, options);
    let result: any = standardisedResult;

    if (standardisedResult.result === true && operation.isFormSubmitOperation() && operation.isServerOperation()) {
      const eventExecutor = this.contentModel.getEventExecutor();

      try {
        if (eventExecutor !== undefined) {
          const onFormSubmitResult = eventExecutor.onFormSubmit(operation, standardisedResult.data);

          if (onFormSubmitResult === false) {
            result = false;
          }
        }
      } catch (ex) {
        console.error(ex);
        (window as any).DialogAPI.error(ex);
        result = false;
      }
    }
    return result;
  }

  private processClientSideAction(tableRecord: any, options: any) {
    let result = true;
    const operation = this.operation;

    // The field name the contextual menu was clicked from - must come with the options
    let fieldName = options.fieldName;

    if (operation.hasClientScript()) {
      let field = null;
      let fieldValue = null;
      let fieldType = null;

      if (fieldName && tableRecord !== null) {
        // Special case for many_to_many and many_to_one relationships as they are not also represented as fields.
        const tableDefinition = (window as any).TableManager.getTableDefinition(this.contentModel.tableModel.getTableName());
        field = tableDefinition.hasRelation(fieldName) &&
        (tableDefinition.getRelation(fieldName).getRelationshipType().toUpperCase() === "MANY_TO_MANY" ||
          tableDefinition.getRelation(fieldName).getRelationshipType().toUpperCase() === "MANY_TO_ONE")
          ? tableDefinition.getRelation(fieldName)
          : tableDefinition.getField(fieldName);

        if (field.getFieldType && (field.getFieldType() === "REFERENCE" || field.getFieldType() === "GENERIC_REFERENCE")) {
          const value = tableRecord.getField(fieldName).getValue();

          if (value !== null && typeof value.id === "function") {
            fieldValue = tableRecord.getField(fieldName).getValue().id();
          } else {
            fieldValue = value;
          }
        } else {
          fieldValue = tableRecord.getFieldValue(fieldName);
        }

        fieldType = field.getFieldType().toLowerCase();
      }

      result = UtilService.evalWithArguments(
        operation.getClientScript(),
        this.getStandardContextItems(
          tableRecord,
          [tableRecord],
          options,
          {
            id: tableRecord?.getID(),
            ids: [tableRecord].map(r => r?.getID()),
            fieldValue: fieldValue,
            fieldType: fieldType,
            queryBuilder: this.contentModel.viewModel.getQueryBuilder(),
            RequestFactory: (window as any).RequestFactory,
            Dialog: (window as any).DialogAPI
          }
        )
      );
    }

    return result;
  }

  private processServerSideAction(tableRecord: any, rawData: any, options?: any) {
    const operation = this.operation;
    const contentModel = this.contentModel;
    const viewDefinitionID = contentModel.viewModel.getID();
    const viewDefinitionName = contentModel.viewModel.getName();

    const serverResponseHandler = (response: any) => {
      this.processOnSuccessAction(response, tableRecord, [tableRecord], rawData);
    };

    const serverErrorResponseHandler = (response) => {
      this.processOnFailureAction(response, tableRecord, [tableRecord], rawData);
    };

    // Prepare server side parameters
    // If we have a parent option, we need to convert it to parentId/parentTable and reconstruct on the server side
    const parameters = {...(options || {})};

    if (parameters.parent) {
      parameters.parentId = parameters.parent.getID();
      parameters.parentTable = parameters.parent.getTableName();
      parameters.parentRelation = options.parentRelation;
      parameters.parent = null;
    }

    // Remove the 'event' argument, as that doesn't make sense to pass to the server
    if (parameters.event) {
      delete parameters.event;
    }

    const tableOperationConfig = {
      record:     rawData,
      operation:  operation.getName(),
      parameters: parameters,
      isNewRecord: tableRecord.isNewRecord(),
      hasChanged: tableRecord.hasChanged(),
      isWorkflowOperation: operation.isWorkflowOperation(),
      workflowActivityData: operation.getWorkflowActivityData()
    };

    const request = this.requestService.createRequest();
    request.controller("servicely.core.System")
      .method("executeTableOperation")
      .viewModel(this.koViewModel)
      .view(viewDefinitionName)
      .viewID(viewDefinitionID)
      .table(contentModel.viewModel.getTableName())
      .data(tableOperationConfig)
      .successFunc(serverResponseHandler);

    if (operation.hasOnFailureScript()) {
      request.errorFunc(serverErrorResponseHandler);
    } else {
      request.errorFunc((response) => {
        const json = response?.responseJSON;
        // Validation errors appear in the errors array as well, so we filter them out.
        let errors: string[] = json?.["errors"] || [];
        let validationErrors: any[] = json?.["validationErrors"] || [];
        errors = errors.filter((e) => validationErrors.filter(ve => e.includes(ve.validationRuleName)).length === 0);

        if (validationErrors.length > 0) {
            validationErrors.forEach((ve) => {
            if (tableRecord.hasField(ve.fieldName)) {
              tableRecord.getField(ve.fieldName).setFieldValidationError(ve.errorString);
            } else {
              errors.push("<b>Validation error</b>: Field is not on the form&nbsp;<b>Field:</b>&nbsp;" + (window as any).KO_INTERNATIONALIZATION_BRIDGE.getMessage(ve.fieldDisplayName) + "&nbsp;<b>Message:&nbsp;</b>" + ve.errorString + "");
            }
          });

          if (errors.length > 0) {
            // We might not want this to be an alert but use the error console instead
            // (window as any).ErrorConsole.setErrors(errors);
            (window as any).DialogAPI.errorMessage(`<pre>${errors.join('\n')}</pre>`);
          }
        }
        this.sendOperationFailureEvent(tableRecord, operation, errors, validationErrors);
      });
    }

    request.execute();
  }

  processBulkServerSideAction(tableRecords: any[], rawData: any[], options?: any) {
    const operation = this.operation;
    const contentModel = this.contentModel;
    const viewDefinitionID = contentModel.viewModel.getID();
    const viewDefinitionName = contentModel.viewModel.getName();

    const serverResponseHandler = (response: any) => {
      const record = tableRecords.length > 0 ? tableRecords[0] : null;
      this.processOnSuccessAction(response, record, tableRecords, rawData);
    };

    const serverErrorResponseHandler = (response) => {
      const record = tableRecords && tableRecords.length > 0 ? tableRecords[0] : null;

      this.processOnFailureAction(response, record, tableRecords, rawData);
    };

    options = options || {};

    // Prepare server side parameters
    // If we have a parent option, we need to convert it to parentId/parentTable and reconstruct on the server side
    const parameters = {...(options || {})};
    if (parameters.parent) {
      parameters.parentId = parameters.parent.getID();
      parameters.parentTable = parameters.parent.getTableName();
      parameters.parent = null;
    }

    const tableOperationConfig = {
      records:    rawData,
      operation:  operation.getName(),
      parameters: parameters,
    };

    const request = this.requestService.createRequest();
    request.controller("servicely.core.System")
      .method("executeBulkTableOperation")
      .viewModel(this.koViewModel) // TODO: this will be one of the records in the bulk selection
      .view(viewDefinitionName)
      .viewID(viewDefinitionID)
      .table(contentModel.viewModel.getTableName())
      .data(tableOperationConfig)
      .successFunc(serverResponseHandler);

    if (operation.hasOnFailureScript()) {
      request.errorFunc(serverErrorResponseHandler);
    }

    request.execute();
  }

  processBulkClientSideAction(tableRecords: any[], options?: any) {
    let result = true;
    const operation = this.operation;

    if (operation.hasClientScript()) {
      let fieldValue = null; // Passed in for compatibility with processClientSideAction

      let tableRecord = tableRecords != null && tableRecords.length > 0 ? tableRecords[0] : null;

      result = UtilService.evalWithArguments(
        operation.getClientScript(),
        this.getStandardContextItems(
          tableRecord,
          tableRecords,
          options,
          {
            id: tableRecord?.getID(),
            ids: tableRecords.map(r => r?.getID()),
            fieldValue: fieldValue,
            queryBuilder: this.contentModel.viewModel.getQueryBuilder(),
            RequestFactory: (window as any).RequestFactory,
            Dialog: (window as any).DialogAPI
          }
        )
      );
    }

    return result;
  }

  private processOnAfterSubmit(tableRecord: any, options: any) {
    const operation = this.operation;
    const eventExecutor = this.contentModel.getEventExecutor();
    if (operation.isFormSubmitOperation() && operation.isServerOperation() && eventExecutor) {
      eventExecutor.afterFormSubmit();
    }
  }

  private processPrepareDataAction(record: any): any {
    const operation = this.operation;
    let result;

    // NOTE: prepare data script does not appear to be used at all. It is not even visible on the form.
    if (operation.hasPrepareDataScript()) {
      const fn = new Function("data", operation.getPrepareDataScript());
      result = fn(record);
    } else {
      result = ContentDataService.getFilteredContentData(record, false, true);
    }

    return result;
  }

  processOnSuccessAction(responseData, tableRecord: any, tableRecords: any[], rawData) {
    const operation = this.operation;
    const contentModel = this.contentModel;
    const updateType = responseData?.data?.updateType;
    // TODO: this comes from the response, which seems wrong
    // TODO: shouldn't it come from the tableRecord parameter?
    // TODO: then we could set the ids param correctly below from tableRecords.
    const id = responseData?.data?.record?.id;
    const formContext = contentModel.viewModel.getFormContext() || null;
    const MessageBus = (window as any).MessageBus;

    if (operation.isFormSubmitOperation()) {
      this.resetOnLoad();
    }

    if (contentModel.viewModel.getViewModelType() === ViewModelType.DETAIL && contentModel.data && contentModel.data.id) {
      contentModel.update(responseData?.data.record, false);
    }

    if (operation.hasOnSuccessScript()) {
      UtilService.evalWithArguments(operation.getOnSuccessScript(),
        this.getStandardContextItems(tableRecord, tableRecords,
          this.options,{
            response:           responseData?.data?.result,
            id:                 id,
            ids:                [id],
            updateType:         updateType
        }));
    }

    // Update list view
    if (contentModel.refresh && Array.isArray(contentModel.data)) {
      contentModel.refresh(id, updateType);
    }

    // Send table update event
    if (updateType) {
      MessageBus.publishTableEvent(contentModel.getTableName(), updateType, id, formContext, tableRecord);
    }

    // Run onFormLoad UI event handlers for detail forms
    if (operation.isFormSubmitOperation() && contentModel.getEventExecutor()) {
      if (this.formType == ViewModelType.DETAIL && !(contentModel instanceof KanbanCardContentModel)) {
        contentModel.getEventExecutor().onFormLoad(contentModel);
      }
    }
  }

  processOnFailureAction(errorResponse, tableRecord: any, tableRecords: any[], rawData) {
    const operation = this.operation;
    const responseJson = errorResponse.responseJSON || {errors: []};

    this.sendOperationFailureEvent(tableRecord, operation, responseJson.errors);

    if (operation.hasOnFailureScript()) {
      UtilService.evalWithArguments(
        operation.getOnFailureScript(),
        this.getStandardContextItems(
          tableRecord,
          tableRecords,
          this.options,
          {
            errors: responseJson.errors
          }
        )
      );
    }
  }

  private sendOperationFailureEvent(tableRecord: any, operation: TableOperationModel, errors: any[] = [], validationErrors: any[] = []) {
    const messageBus = (window as any).MessageBus;
    messageBus.publish(
      messageBus.CHANNELS.OPERATION_EVENT,
      messageBus.OPERATION_EVENTS.FAILED,
      {
        id: tableRecord?.getID(),
        tableName: tableRecord?.getTableName(),
        operation: {
          code: operation?.getCode(),
          id: operation?.getID()
        },
        errors: errors,
        validationErrors: validationErrors
      }
    );
  }

  private resetOnLoad() {
    this.contentModel.clearJournalEntries();
  }

  /**
   * This method combines the generation of the KO TableRecord with the creation of the KO ListViewModel
   * as those are both created (at different times) in the KO code. We do this because it is the only way
   * to get them both to match what the KO code generates, so we can execute the table operations without
   * creating KO objects up front.
   *
   * Note that we ignore much of what is done in the initialisation as it seems redundant for list view,
   * marked with 'TODO' below, however some of it will be be needed for detail view.
   *
   * If 'transient' is true then we are only using this method to generate the table record
   */
  private getTableRecordFromRawData(record: any, contentModel: ContentModel, transient: boolean = false): any {
    if (record == null || Array.isArray(record) || !this.tableName) {
      return null;
    }

    let tableRecord;

    let isListContentModel = (contentModel instanceof ListContentModel);
    let isKanbanCardContentModel = (contentModel instanceof KanbanCardContentModel);

    if (isListContentModel || contentModel.viewModel.getViewModelType() == ViewModelType.LIST || isKanbanCardContentModel) {
      const koViewModel = this.buildKoViewModel(record, contentModel, transient);

      // This is cached as it is used elsewhere. Note that this will be only one of the records if we do a bulk operation.
      if (!transient) {
        if (!this.koViewModel) {
          this.koViewModel = koViewModel;
        }
      }

      tableRecord = (window as any).TableRecord.getTableRecord(this.tableName, koViewModel.data, koViewModel);
    } else {
      let detailViewModel = (contentModel as DetailContentModel).getDetailViewModel();

      // For detail forms we only support table operations in the content footer at the moment.
      if (!transient) {
        // Use KO UI event executor
        if ((window as any).KO_UI_EVENT_EXECUTOR.viewModelHasUIEventExecutor(detailViewModel)) {
          // 1. Borrow the event executor
          contentModel.setEventExecutor(detailViewModel.getEventExecutor());

          // // 2. Don't run the onFormLoad UI events for detail forms as they will have been done in the KO code.
          // if (this.formType == ViewModelType.DETAIL) {
          //   detailViewModel.getEventExecutor().onFormLoad(detailViewModel);
          // }
        }
      }

      tableRecord = (window as any).TableRecord.getTableRecord(this.tableName, record, detailViewModel);
    }

    // fix the Table type if it is present (otherwise it may be a parent table name).
    if (tableRecord._data && tableRecord.hasField("TableClass")) {
      tableRecord._data._tableClass = tableRecord.getFieldValue("TableClass");
    }

    return tableRecord;
  }

  // TODO: koViewModel is the NG equivalent of contentModel.
  private buildKoViewModel(record: any, contentModel: ContentModel, transient: boolean = false) {
    const autogeneratedViewModelFactory = (window as any).KO_AUTOGENERATED_VIEW_MODEL_FACTORY;

    const viewDefinition = contentModel.viewModel.viewDefinition();

    // tile_view.js ...
    const koMappedViewModel = autogeneratedViewModelFactory.createViewModelFromViewDefinition(viewDefinition, record);

    const viewModelName = viewDefinition.viewModelType; // e.g. list

    const viewModelFactory = (viewModelName === "list") ?
      (window as any).KO_LIST_VIEW_MODEL_FACTORY :
      (window as any).KO_DETAIL_VIEW_MODEL_FACTORY;

    // KO version of the list view model - equivalent to contentModel - do we even need to build this?
    // TODO: remove all the KO stuff from the old list that is still in use
    const koViewModel = viewModelFactory.createViewModel();

    // TODO: parentViewModel ignored - only appears to be used for required fields popup on a form.

    // koViewModel.onLoad call in (list.js) - only relevant stuff pulled out from this call
    koViewModel.aspect = viewDefinition.aspect;
    koMappedViewModel._setFormDisplayType(contentModel.viewModel.getFormDisplayType());
    Object.assign(koViewModel, koMappedViewModel);

    // TODO: do we need to set tableDefinition on koViewModel?

    // tile_view.js
    if (koViewModel._setFormContext) {
      koViewModel._setFormContext(contentModel.viewModel.getFormContext());
    }

    if (koViewModel._setParentContext) {
      koViewModel._setParentContext(contentModel.viewModel.getParentContext());
    }

    if (koViewModel._setIsRecordSyncDisabled) {
      koViewModel._setIsRecordSyncDisabled(contentModel.viewModel.getIsRecordSyncDisabled());
    }

    // TODO: do we need to set beforeApplyViewModelCallback?

    // tile_view.js applyViewModel
    if (koViewModel.data && koViewModel.data.hasOwnProperty('_tableClass')) {
      koViewModel._viewName = viewDefinition.viewName;
    }

    // TODO: ignore setIsLoading

    // TODO: ignore ko.applyBindings

    // TODO: ignore _unloadEvent

    // TODO: ignore suppressBeforePageChangeListener

    // TODO: ignore koViewModel.onBind

    if (!transient) {
      // Use KO UI event executor
      if ((window as any).KO_UI_EVENT_EXECUTOR.viewModelHasUIEventExecutor(koMappedViewModel)) {
        // 1. Borrow the event executor
        contentModel.setEventExecutor(koMappedViewModel.getEventExecutor());

        // 2. Run the onFormLoad UI events for detail forms
        if (this.formType == ViewModelType.DETAIL) {
          koMappedViewModel.getEventExecutor().onFormLoad(koMappedViewModel);
        }
      }
    }

    // TODO: ignore _beforeRenderViewCallback for now

    // TODO: ignore _setVisibleCallback

    return koViewModel;
  }

  private getStandardContextItems(record: any, records: any[], options: any, operationSpecificOptions?: any): any {
    const viewDefinition = this.contentModel instanceof DetailContentModel ?
      (this.contentModel as DetailContentModel).getDetailViewModel()._viewDefinition :
      this.contentModel._viewDefinition; //List, Kanban

    const scriptContext = {
      current:            record,
      currents:           records,
      tableName:         this.tableName,
      formContext:        this.contentModel.viewModel.getFormContext(),
      parentContext:      this.contentModel.viewModel.getParentContext(),
      formType:           this.formType,
      TableManager:       (window as any).TableManager,
      viewAspect:         this.contentModel.viewModel.getAspect(),
      viewDefinitionID:   this.contentModel.viewModel.getID(),
      viewDefinition:     viewDefinition,
      isWorkflowOperation: this.operation.isWorkflowOperation(),
      workflowActivityData: this.operation.getWorkflowActivityData()
    };

    return {...scriptContext, ...(options || {}), ...(operationSpecificOptions || {})};
  }
}
